/**
 * SessionPreview — teacher preview that IS the student lesson viewer.
 *
 * The previous preview was a second,
 * hand-maintained renderer with its own theme palette and section markup, so
 * it drifted from what students actually see. This one imports the student's
 * `LessonView` directly: same layout, same section variants, same card
 * flip / hint / answer behaviour, same LaTeX rendering. There is no second
 * copy to keep in sync — a change to the student viewer changes this preview.
 *
 * Two things have to be supplied that the student app normally provides:
 *
 *   1. Theme CSS variables. LessonView styles everything off `var(--st-*)`,
 *      which only exist inside the student shell. We generate them with the
 *      same `getTheme` + `themeToCssVars` helpers StudentApp uses, so colours,
 *      fonts and radii resolve identically rather than falling back to
 *      unstyled defaults.
 *   2. An attachment URL builder. The student cards default to the
 *      enrollment-gated `/student/mydesk/...` route; a teacher is not enrolled
 *      in their own class, so that 403s and every attachment renders as
 *      "Preview unavailable". `AttachmentUrlContext` swaps in the owner-gated
 *      teacher route for the preview only.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';

import LessonView from '../../../pages/student/mydesk/lesson_views/LessonView';
import { AttachmentUrlContext } from '../../../pages/student/mydesk/lesson_views/StudentAttachmentCards';
import { getTheme, themeToCssVars, DEFAULT_THEME_ID } from '../../../pages/student/themes';
import { LessonPreviewSkeleton } from '../TeacherSkeletons';

const API = process.env.REACT_APP_BACKEND_URL;

// Owner-gated twin of the student attachment route. Same `?auth=` query-token
// contract, because <img>/<video>/<iframe> cannot send an Authorization header.
const buildTeacherAttachmentUrl = (attachmentId) => {
  const token = localStorage.getItem('desky_token') || '';
  return `${API}/api/teacher/studio/attachment/${attachmentId}/download?auth=${encodeURIComponent(token)}`;
};

/**
 * Shape a studio draft the way the student lesson endpoint returns a published
 * lesson, so LessonView receives exactly the object it was written against.
 * Attachments already live inline on `framework.sections[].attachments`, which
 * is what the student payload carries too — no extra fetch needed.
 */
export function draftToLesson(draft, framework, sections, references) {
  const fw = framework || draft?.framework || {};
  return {
    ...draft,
    title: draft?.title || fw.title || 'Untitled lesson',
    summary: draft?.summary || fw.description || '',
    framework: fw,
    // LessonView prefers `framework_sections`; pass the editor's normalised
    // sections so preview reflects unsaved in-editor shape fixes.
    framework_sections: sections && sections.length ? sections : (fw.sections || []),
    references: references || draft?.references || [],
  };
}

export default function SessionPreview({ draft, framework, sections, references = [], onClose }) {
  const lesson = useMemo(
    () => draftToLesson(draft, framework, sections, references),
    [draft, framework, sections, references],
  );

  // Mirror StudentApp's shell so LessonView renders against real theme vars.
  const theme = useMemo(() => getTheme(draft?.preview_theme_id || DEFAULT_THEME_ID), [draft]);
  const cssVars = useMemo(() => themeToCssVars(theme, null, 1, null), [theme]);

  // The first LessonView render (every section + LaTeX) is heavy enough to
  // freeze the click with no feedback. Paint a loading screen first, then
  // mount the lesson two frames later — after the loader is on screen.
  const [lessonReady, setLessonReady] = useState(false);
  useEffect(() => {
    let secondFrame;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => setLessonReady(true));
    });
    return () => {
      cancelAnimationFrame(firstFrame);
      cancelAnimationFrame(secondFrame);
    };
  }, []);

  if (!framework && !draft?.framework) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" data-testid="session-preview">
      <div
        className="min-h-full w-full"
        style={{
          ...cssVars,
          background: 'var(--st-bg)',
          color: 'var(--st-ink)',
          fontFamily: 'var(--st-font-body)',
        }}
        data-theme-id={theme.id}
        data-theme-tier={theme.tier}>
        {/* Headings pick up the theme font exactly as they do in the student
            shell, which applies this rule globally rather than per-component. */}
        <style>{`
          [data-testid="session-preview"] h1,
          [data-testid="session-preview"] h2,
          [data-testid="session-preview"] h3,
          [data-testid="session-preview"] h4 {
            font-family: var(--st-font-heading) !important;
            letter-spacing: var(--st-letter-spacing, 0);
          }
        `}</style>

        <header
          className="sticky top-0 z-40 backdrop-blur border-b"
          style={{ background: 'var(--st-card)', borderColor: 'var(--st-border)' }}>
          <div className="max-w-3xl mx-auto px-6 sm:px-10 h-14 flex items-center gap-3">
            <span className="text-xs font-semibold " style={{ color: 'var(--st-primary)' }}>
              Student view
            </span>
            <p className="text-xs truncate" style={{ color: 'var(--st-mute)' }}>
              This is exactly what students see.
            </p>
            <button
              onClick={onClose}
              className="ml-auto w-8 h-8 rounded-lg flex items-center justify-center transition-colors"
              style={{ background: 'var(--st-bg)', color: 'var(--st-mute)' }}
              data-testid="close-preview"
              aria-label="Close preview">
              <X className="w-4 h-4" />
            </button>
          </div>
        </header>

        {lessonReady ? (
          <AttachmentUrlContext.Provider value={buildTeacherAttachmentUrl}>
            <LessonView lesson={lesson} />
          </AttachmentUrlContext.Provider>
        ) : (
          <LessonPreviewSkeleton />
        )}
      </div>
    </div>
  );
}
